<script setup>
// Modal de criar/editar perfil de acesso.
//
// Ate 04/09/2026 este formulario vivia dentro do cartao de detalhes da
// pagina de perfis: clicar em "Novo perfil" limpava o cartao e transformava-o
// em formulario, entao o mesmo espaco era, ao mesmo tempo, a ficha do perfil
// escolhido e o formulario do perfil novo. A pedido, o cartao passou a ser so
// leitura e toda a edicao mudou-se para aqui.
//
// O desenho (Teleport, overlay, cabecalho, rodape) segue o AgentFormModal da
// mesma pasta, para os dois modais da pagina de agentes se lerem como um so.
import { computed, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AccessProfilePermissionsGrid from './AccessProfilePermissionsGrid.vue';
import {
  ACCESS_MODULES,
  INTEGRATION_MANAGEMENT_PERMISSIONS,
  CONVERSATION_SCOPE_PERMISSIONS,
  buildCustomRolePermissions,
  extractAdditionalPermissions,
  extractConversationActions,
  extractConversationScope,
  extractPagePermissions,
  extractPreservedPermissions,
} from './accessProfilesCatalog';

const props = defineProps({
  // `null` cria um perfil; um objeto edita o perfil recebido.
  profile: { type: Object, default: null },
  profiles: { type: Array, default: () => [] },
  copy: { type: Object, required: true },
  userCount: { type: Number, default: 0 },
});

const emit = defineEmits(['close', 'saved']);

const VIEW_DEPENDENT_CONVERSATION_ACTIONS = [
  'conversation_create',
  'conversation_reply',
  'conversation_claim',
  'conversation_transfer',
  'conversation_resolve',
  'conversation_reopen',
];

const store = useStore();
const uiFlags = useMapGetter('customRole/getUIFlags');

const name = ref('');
const description = ref('');
const baseProfileId = ref('');
const confirmationDialog = ref(null);
const conversationActions = ref([]);
const pagePermissions = ref([]);
const preservedConversationScope = ref('');
const preservedConversationScopes = ref([]);
const preservedPermissions = ref([]);
const selectedPermissions = ref([]);
const selectedModuleId = ref('conversations');

const isEditing = computed(() => Boolean(props.profile?.id));

const isSaving = computed(
  () => uiFlags.value.creatingItem || uiFlags.value.updatingItem
);

const submitIcon = computed(() =>
  isEditing.value ? 'i-lucide-check' : 'i-lucide-shield-plus'
);

const title = computed(() =>
  isEditing.value
    ? props.copy.editDetailCardTitle
    : props.copy.newDetailCardTitle
);

const availableBaseProfiles = computed(() =>
  props.profiles.filter(profile => {
    const normalizedName = (profile.name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLocaleLowerCase();

    return (
      profile.id && !profile.immutable && normalizedName !== 'proprietario'
    );
  })
);

const populatePermissions = permissions => {
  conversationActions.value = extractConversationActions(permissions);
  pagePermissions.value = extractPagePermissions(permissions);
  preservedConversationScope.value = extractConversationScope(permissions);
  preservedConversationScopes.value = permissions.filter(permission =>
    CONVERSATION_SCOPE_PERMISSIONS.includes(permission)
  );
  preservedPermissions.value = extractPreservedPermissions(permissions);
  selectedPermissions.value = extractAdditionalPermissions(permissions);
};

const populateForm = profile => {
  name.value = profile?.name || '';
  description.value = profile?.description || '';
  baseProfileId.value = '';
  populatePermissions(profile?.permissions || []);
  selectedModuleId.value = 'conversations';
};

// O modal e montado so quando aberto (v-if no pai), mas trocar de perfil com
// ele aberto — editar outro sem fechar — tem de repovoar o formulario.
watch(() => props.profile, populateForm, { immediate: true });

const applyBaseProfile = () => {
  const baseProfile = availableBaseProfiles.value.find(
    profile => String(profile.id) === baseProfileId.value
  );
  populatePermissions(baseProfile?.permissions || []);
};

const selectedModule = computed(() =>
  ACCESS_MODULES.find(module => module.id === selectedModuleId.value)
);

const selectModule = module => {
  selectedModuleId.value = module.id;
};

const setModulePages = (module, allowed) => {
  const modulePagePermissions = module.pages.map(page => page.permission);
  pagePermissions.value = allowed
    ? [...new Set([...pagePermissions.value, ...modulePagePermissions])]
    : pagePermissions.value.filter(
        permission => !modulePagePermissions.includes(permission)
      );

  if (module.id === 'conversations') {
    conversationActions.value = allowed
      ? [
          ...new Set([
            ...conversationActions.value,
            'conversation_view',
            'conversation_page_access',
          ]),
        ]
      : [];
    return;
  }
  const permission = module.permission;
  selectedPermissions.value = allowed
    ? [...new Set([...selectedPermissions.value, permission])]
    : selectedPermissions.value.filter(item => item !== permission);
};

const setSelectedModulePages = allowed =>
  setModulePages(selectedModule.value, allowed);

const selectedModuleAllowed = computed(() =>
  selectedModule.value.pages.some(page =>
    pagePermissions.value.includes(page.permission)
  )
);

const togglePage = page => {
  const isAllowed = pagePermissions.value.includes(page.permission);
  pagePermissions.value = isAllowed
    ? pagePermissions.value.filter(permission => permission !== page.permission)
    : [...pagePermissions.value, page.permission];

  if (selectedModuleId.value === 'integrations') {
    if (
      !isAllowed &&
      INTEGRATION_MANAGEMENT_PERMISSIONS.includes(page.permission)
    ) {
      pagePermissions.value = [
        ...new Set([...pagePermissions.value, 'page_integrations']),
      ];
    } else if (isAllowed && page.permission === 'page_integrations') {
      pagePermissions.value = pagePermissions.value.filter(
        permission => !INTEGRATION_MANAGEMENT_PERMISSIONS.includes(permission)
      );
    }
  }

  const hasModulePage = selectedModule.value.pages.some(item =>
    pagePermissions.value.includes(item.permission)
  );
  if (selectedModuleId.value === 'conversations') {
    if (hasModulePage) {
      conversationActions.value = [
        ...new Set([
          ...conversationActions.value,
          'conversation_view',
          'conversation_page_access',
        ]),
      ];
    } else {
      conversationActions.value = [];
    }
    return;
  }

  const permission = selectedModule.value.permission;
  selectedPermissions.value = hasModulePage
    ? [...new Set([...selectedPermissions.value, permission])]
    : selectedPermissions.value.filter(item => item !== permission);
};

const toggleConversationAction = actionId => {
  const selected = conversationActions.value.includes(actionId);
  if (selected) {
    if (actionId === 'conversation_page_access') {
      conversationActions.value = [];
      setSelectedModulePages(false);
      return;
    }
    if (actionId === 'conversation_view') {
      setSelectedModulePages(false);
      return;
    }
    conversationActions.value = conversationActions.value.filter(
      permission => permission !== actionId
    );
    return;
  }

  const requiredPermissions = ['conversation_page_access', 'conversation_view'];
  if (actionId !== 'conversation_page_access') {
    requiredPermissions.push(actionId);
  }
  if (VIEW_DEPENDENT_CONVERSATION_ACTIONS.includes(actionId)) {
    requiredPermissions.push('conversation_view');
  }
  conversationActions.value = [
    ...new Set([...conversationActions.value, ...requiredPermissions]),
  ];
  if (!selectedModuleAllowed.value) setSelectedModulePages(true);
};

const toggleAction = action => {
  if (selectedModuleId.value === 'conversations') {
    toggleConversationAction(action.id);
    return;
  }
  setSelectedModulePages(!selectedModuleAllowed.value);
};

const confirmationTitle = 'Alterar permissões';

async function saveProfile() {
  if (isSaving.value) return;
  confirmationDialog.value?.close();
  const permissions = buildCustomRolePermissions({
    conversationActions: conversationActions.value,
    selectedPermissions: selectedPermissions.value,
    pagePermissions: pagePermissions.value,
    preservedPermissions: preservedPermissions.value,
    preservedConversationScope: preservedConversationScope.value,
    preservedConversationScopes: preservedConversationScopes.value,
  });
  if (!name.value.trim()) {
    useAlert(props.copy.requiredFields);
    return;
  }

  const payload = {
    name: name.value.trim(),
    description: description.value.trim() || null,
    permissions,
  };

  try {
    const savedProfile = isEditing.value
      ? await store.dispatch('customRole/updateCustomRole', {
          id: props.profile.id,
          ...payload,
        })
      : await store.dispatch('customRole/createCustomRole', payload);
    useAlert(
      isEditing.value ? props.copy.updateSuccess : props.copy.createSuccess
    );
    emit('saved', savedProfile);
    emit('close');
  } catch (error) {
    useAlert(error?.message || props.copy.apiError);
  }
}
const requestSave = () => {
  if (!name.value.trim()) {
    useAlert(props.copy.requiredFields);
    return;
  }
  if (isEditing.value && props.userCount > 0) {
    confirmationDialog.value.open();
    return;
  }
  saveProfile();
};
</script>

<template>
  <Dialog
    ref="confirmationDialog"
    type="alert"
    :title="confirmationTitle"
    :description="`Isso altera o acesso de ${userCount} usuários.`"
    :confirm-button-label="copy.save"
    :cancel-button-label="copy.cancel"
    :is-loading="isSaving"
    @confirm="saveProfile"
  />
  <Teleport to="body">
    <!-- Aceleres: a casca e as classes sao as do AgentFormModal (o modal de
         novo usuario) de proposito — os dois modais da pagina de agentes tem
         de se ler como o mesmo componente. O que muda e so o conteudo. -->
    <div
      class="access-profile-modal agent-form-modal agent-form-modal--overlay fixed inset-0 z-[9999] flex w-full items-end justify-center bg-n-alpha-black2 backdrop-blur-[4px] sm:items-center sm:p-4"
      @mousedown.self="emit('close')"
    >
      <form
        class="agent-form-modal__dialog access-profile-modal__dialog flex max-h-[100dvh] w-full max-w-[960px] flex-col overflow-hidden border border-n-weak bg-n-solid-1 shadow-xl max-sm:h-[100dvh] max-sm:border-x-0 max-sm:border-b-0 sm:max-h-[calc(100dvh-2rem)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @submit.prevent="requestSave"
      >
        <header
          class="agent-form-modal__header access-profile-modal__header flex flex-shrink-0 items-start justify-between gap-4 border-b border-n-weak px-6 py-5 max-sm:px-4 max-sm:py-4 sm:px-8 sm:py-6"
        >
          <div class="agent-form-modal__header-text min-w-0">
            <h2
              class="agent-form-modal__title access-profile-modal__title m-0 text-xl font-semibold text-n-slate-12"
            >
              {{ title }}
            </h2>
            <p
              class="agent-form-modal__subtitle mb-0 mt-1 text-sm leading-5 text-n-slate-11"
            >
              {{ copy.modalDescription }}
            </p>
          </div>
          <Button
            ghost
            slate
            type="button"
            icon="i-lucide-x"
            class="agent-form-modal__close"
            :disabled="isSaving"
            :aria-label="copy.cancel"
            @click="emit('close')"
          />
        </header>

        <div
          class="agent-form-modal__body access-profile-modal__body scrollbar-thin flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          <section
            class="agent-form-modal__identity flex flex-col border-b border-n-weak px-6 py-7 max-sm:px-4 max-sm:py-5 sm:px-8"
          >
            <div
              class="agent-form-modal__fields grid gap-x-6 gap-y-5 max-sm:gap-y-4 sm:grid-cols-2"
            >
              <label
                class="agent-form-modal__field agent-form-modal__field--name m-0 block min-w-0 text-sm font-medium text-n-slate-12"
              >
                {{ copy.nameLabel }}
                <input
                  v-model="name"
                  type="text"
                  class="agent-form-modal__input reset-base mt-2 h-11 w-full rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="copy.namePlaceholder"
                  :readonly="Boolean(profile?.system_key)"
                />
              </label>

              <label
                v-if="!isEditing"
                class="agent-form-modal__field agent-form-modal__field--base-profile m-0 block min-w-0 text-sm font-medium text-n-slate-12"
              >
                {{ copy.baseProfileLabel }}
                <span class="relative mb-4 mt-2 block">
                  <select
                    v-model="baseProfileId"
                    class="agent-form-modal__input reset-base !mb-0 h-11 w-full appearance-none rounded-lg border border-solid border-n-weak !bg-none bg-n-solid-1 px-3 !pr-9 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand rtl:!pl-9 rtl:!pr-3"
                    @change="applyBaseProfile"
                  >
                    <option value="">{{ copy.noBaseProfile }}</option>
                    <option
                      v-for="baseProfile in availableBaseProfiles"
                      :key="baseProfile.id"
                      :value="String(baseProfile.id)"
                    >
                      {{ baseProfile.name }}
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10 rtl:left-3 rtl:right-auto"
                  />
                </span>
              </label>

              <label
                class="agent-form-modal__field agent-form-modal__field--description m-0 block min-w-0 text-sm font-medium text-n-slate-12 sm:col-span-2"
              >
                {{ copy.descriptionLabel }}
                <textarea
                  v-model="description"
                  rows="3"
                  class="agent-form-modal__input reset-base mt-2 w-full resize-none rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="copy.descriptionPlaceholder"
                />
              </label>
            </div>
          </section>

          <section
            class="agent-form-modal__permissions flex flex-col px-6 py-7 max-sm:px-4 max-sm:py-5 sm:px-8"
          >
            <div class="agent-form-modal__permissions-heading mb-6 max-sm:mb-5">
              <h3
                class="agent-form-modal__teams-title m-0 text-base font-semibold text-n-slate-12"
              >
                {{ copy.permissionsTitle }}
              </h3>
              <p
                class="agent-form-modal__teams-description mb-0 mt-1 text-sm leading-5 text-n-slate-11"
              >
                {{ copy.permissionsDescription }}
              </p>
            </div>

            <AccessProfilePermissionsGrid
              :selected-module-id="selectedModuleId"
              :page-permissions="pagePermissions"
              :conversation-actions="conversationActions"
              :labels="copy"
              @select-module="selectModule"
              @toggle-module="setModulePages"
              @toggle-page="togglePage"
              @toggle-action="toggleAction"
            />
          </section>
        </div>

        <footer
          class="agent-form-modal__footer access-profile-modal__footer flex flex-shrink-0 items-center justify-end gap-3 border-t border-n-weak bg-n-solid-1 px-6 py-5 max-sm:flex-col max-sm:items-stretch max-sm:px-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:pt-3 sm:px-8"
        >
          <div
            class="agent-form-modal__footer-actions flex flex-shrink-0 items-center gap-2 max-sm:grid max-sm:w-full max-sm:grid-cols-2"
          >
            <Button
              faded
              slate
              type="button"
              class="agent-form-modal__cancel max-sm:w-full"
              :label="copy.cancel"
              :disabled="isSaving"
              @click="emit('close')"
            />
            <Button
              type="submit"
              class="agent-form-modal__submit max-sm:w-full"
              :icon="submitIcon"
              :label="isSaving ? copy.saving : copy.save"
              :disabled="isSaving"
              :is-loading="isSaving"
            />
          </div>
        </footer>
      </form>
    </div>
  </Teleport>
</template>
